
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';

import { debounce, sum } from 'lodash';

import { addMinutes } from 'date-fns';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation  } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import CopyAllIcon from '@mui/icons-material/CopyAll';

import CheckIcon from '@mui/icons-material/Check';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import QueueIcon from '@mui/icons-material/Queue';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';


import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import ArchiveIcon from '@mui/icons-material/Archive';

import FastfoodIcon from '@mui/icons-material/Fastfood';

import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WashIcon from '@mui/icons-material/Wash';

import { MdOutlineAdd, MdOutlineCancel, MdOutlineCheckCircle } from 'react-icons/md';


import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom,currentUserSessionAtom, 
      isSearchBoxShowAtom, isSaveLoadingAtom, isWaitingBoxShowAtom } from 'library/store';
import useBillingService, { useBasicFilterBilling } from './services/Billing';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { defaultFeatureDescription, IEntity, IFeatureDescription, IFeatureParameter, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Link, FormHelperText } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IBilling, IBillingDelivery, IBillingDeliveryDetail, IBillingDetail, IBillingDetailInfo, 
        IBillingInfo, IBillingPackagingDetail, IBillingRentalResource, IBillingServiceTask, ICommissionDistribution, 
        defaultBilling, defaultBillingDelivery, defaultBillingDetail } from './models/Billing';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration,  { IEnumerationItem, Enum_SERVICE_TASK, Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE,
  Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_PRICE_PURPOSE, 
  Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_CORPORATION_TYPE, 
  Enum_RENTAL_PERIODICITY,
  Enum_CURRENCY, Enum_PAYMENT_SOURCE,
  Enum_CONSUMPTION_OPTION,
  Enum_BILLING_TERM_REFERENCE,
  defaultEnumerationItem} from 'features/configuration/models/Enumeration';

import useProductService, { useBasicFilterProduct, useBasicFilterProductWithOptions } from 'features/setup/services/Product';

import usePersonService, {useBasicFilterPerson, useBasicFilterPersonSearch}  from 'features/production/services/Person';

import { IProduct, IProductOption } from 'features/setup/models/Product';

import { carouselImage, justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { IPerson, PersonType, defaultPerson } from 'features/production/models/Person';

import useUtils from 'library/utils';

import { useBasicFilterEmployee } from 'features/setup/services/Employee';
import { IEmployee } from 'features/setup/models/Employee';
import { IPackaging } from 'features/setup/models/Packaging';
import { useBasicFilterPackaging } from 'features/setup/services/Packaging';
import { GrClose, GrSearch } from 'react-icons/gr';
import EnhancedTable from 'components/ui/EnhancedTable';
import { NestCamWiredStandTwoTone } from '@mui/icons-material';
import ArrayField from 'components/ui/ArrayField';
import { title } from 'process';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';
import { defaultStore } from 'features/configuration';
import { IStore } from 'features/configuration/models/Store';
import { BasicBillingDetailForm } from './BasicBillingDetailForm';
import { BasicBillingInfoForm } from './BasicBillingInfoForm';

export const BillingForm: FC<IBilling> = (props: IBilling = defaultBilling) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {id} = useParams();

  const {range, addDurationInPeriod} = useUtils();

  const {language: lg, taskCodes, userDescription, userName, 
      employeeId, roleEntities, applicationSetup, userStores} = useRecoilValue(currentUserSessionAtom);
  
  const [isWaitingBoxShow, setIsWaitingBoxShow] = useRecoilState(isWaitingBoxShowAtom);

  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const {retrieveEntity, retrieveData, openEntityActionDrawer, executeEntityFeature,
      checkEntityExpressionSyntax, checkEntitySaveAuthorization, openEntityPrintDrawer } = entityService();

  const {getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();

  const { getPersonsSearchCount, getBirthDateType } = usePersonService();

  const { getProductOptionsByBilling } = useProductService();

  const { createBilling, updateBilling, evaluateAddedProduct, getCommissionDistributionsByBilling, deliverBillingDetail,
          createBillingDelivery, updateBillingDelivery, getBillingDeliveriesByBilling } = useBillingService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterBilling = useBasicFilterBilling( 
    (event: React.MouseEvent<unknown>, row: IBilling) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );

  const emptyFunc = (obj: any) => {}
  
  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity( 
      (event: React.MouseEvent<unknown>, row: IEntity) => {
          const {name, description} = row;

          // setValue('entityName', name || '');
          // setValue('entityDescription', description || '');
                           
          setOpenEntityFilter(false);
      }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;

          // setValue('enumerationId', id);
          // setValue('enumerationName', name);
                           
          setOpenEnumerationFilter(false);
      }
  );

  const {canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  const [openPersonFilter, setOpenPersonFilter] = useState(false);
  const basicFilterPerson = useBasicFilterPerson( 
      (event: React.MouseEvent<unknown>, row: IPerson) => {
          const {id, type, firstName, lastName, birthDate, birthPlace,  birthDateType,
            particularSign, corporationName, corporationType, creationDate, creationDateType,
            portable1, portable2, email1, email2} = row;
        
        if(currentPersonUsage === 'customer') {
          setValue('customerId', id);

          setValue('customer', row);
          // setValue('customerPersonType', type);

          // setValue('customerFirstName', firstName);
          // setValue('customerLastName', lastName); 
          // setValue('customerPortable1', portable1);
          // setValue('customerPortable2', portable2);
          // setValue('customerEmail1', email1);
          // setValue('customerEmail2', email2);
          // setValue('customerCorporationName', corporationName);
          // setValue('customerCorporationType', corporationType);
          // setValue('customerBirthDate', birthDate);
          // setValue('customerBirthDateType', birthDateType);
          // setValue('customerCreationDate', creationDate);
          // setValue('customerCreationDateType', creationDateType);

        }  
       
        setOpenPersonFilter(false);
      }
  );

  const [products, setProducts] = useState<IProduct[]>([]);

  const [allProductOptions, setAllProductOptions] = useState<IProductOption[]>([]);

  const refCurrencyCode = useRef<string>('');
  const refShowroomId = useRef<number>(-1);

  const refProductFilter = useRef<number>(0);

  const [openProductFilter, setOpenProductFilter] = useState(false); 
  const basicFilterProduct = useBasicFilterProductWithOptions ( refProductFilter.current, refShowroomId.current, refCurrencyCode.current, 
    async (event: React.MouseEvent<unknown>, row: IProduct) => {
        const {id, description,name, serviceDurationInMinute, isActive ,lineOfBusinessCode, productOptions, type} = row;

        if(!isActive) {
          enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
          return;
        }

        setIsWaitingBoxShow(true);
        const res = await evaluateAddedProduct({...getValues(),productId: id, billingDetail: {...defaultBillingDetail}, 
                currencyCode: watchCurrencyCode, storeId: watchShowroomId});
        setIsWaitingBoxShow(false);

        const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingServiceTasks} = res.data;

              
        const countBillingDetail = getValues().billingDetails.length;
        (refAppendBillingDetails.current??emptyFunc)({...res.data, type, productId: id, productOptions,  
          productName: name, lineOfBusinessCode,
          beneficiaryIsCustomer: true, birthDateType: 'date' } as unknown as IBillingDetail);                          
                    
        setTotalAmount();
        billingDetailSelected(countBillingDetail);
        //setProductIndex(countBillingDetail);
        setOpenProductFilter(false);
    }
);

  const [openServiceFilter, setOpenServiceFilter] = useState(false); // 1
  const basicFilterService = useBasicFilterProductWithOptions ( refProductFilter.current, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive ,lineOfBusinessCode, productOptions} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(),productId: id, billingDetail: {...defaultBillingDetail}, 
                  currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);

          const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingServiceTasks} = res.data;

          for(var i = 0; i<billingServiceTasks.length; i++ ) {
            const billingServiceTask = billingServiceTasks[i];
            if( employeeId > 0 && taskCodes.includes(billingServiceTask.taskCode)) {
              billingServiceTask.employeeId = employeeId;
              billingServiceTask.employeeFullName = `${userDescription} - ${userName}`;
            }
          }
        
          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'service', productId: id, productOptions,  
            productName: name, lineOfBusinessCode,
            beneficiaryIsCustomer: true, billingServiceTasks, birthDateType: 'date' } as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenServiceFilter(false);
      }
  );


  const [openFeeFilter, setOpenFeeFilter] = useState(false); // 7
  const basicFilterFee = useBasicFilterProductWithOptions( refProductFilter.current, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive, lineOfBusinessCode, productOptions} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail},
                                          currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);

          const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity} = res.data;
                  
          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'fee', productId: id, productOptions, 
            productName: name, lineOfBusinessCode} as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenFeeFilter(false);
      }
  );

  const [openArticleFilter, setOpenArticleFilter] = useState(false); // 2
  const basicFilterArticle = useBasicFilterProductWithOptions( refProductFilter.current, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive, filterOption, lineOfBusinessCode,
            defaultPrice, defaultMaxDiscount, defaultMaxLoaded, priceExpression, maxDiscountExpression, maxLoadedExpression,
            productOptions, isTaxable
          } = row;

          const st = store ? store! : defaultStore;
          
          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          if(productOptions.length <= 0) {
            enqueueSnackbar( `${t('This product must have at least one option')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          if(!productOptions.some(opt => opt.isAvailable) && st.explicitProductDefinition) {
            enqueueSnackbar( `${t('This product must have at least one option')} : ${t('availability')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          if(!products.some(p => p.id === id))
            setProducts([...products, {...row}]); // append new products in the list of products.

          const countBillingDetail = getValues().billingDetails.length;
          const tax = isTaxable ? (getValues().defaultTaxRate*defaultPrice/100) : 0;
          
          if(isFalsy(priceExpression) && isFalsy(maxDiscountExpression) && isFalsy(maxLoadedExpression) ) {
            (refAppendBillingDetails.current??emptyFunc)({...defaultBillingDetail, type: 'article', productId: id,
              productOptions,
              quantity: 1,
              unityAmount: defaultPrice, baseAmount: defaultPrice, netAmount: defaultPrice, tax, amount: defaultPrice+tax,
              maxDiscountAmount: defaultMaxDiscount, maxLoadedAmount: defaultMaxLoaded,
              productName: name, lineOfBusinessCode, 
              productOptionId: productOptions.find(opt => opt.isAvailable || !st.explicitProductDefinition )!.id,
              articleFilterOption: '', articleOption:'' } as unknown as IBillingDetail); 
          } else { // price is expression.

            setIsWaitingBoxShow(true);
            const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail, quantity: 1},
                                        currencyCode: watchCurrencyCode, storeId: watchShowroomId});
            setIsWaitingBoxShow(false);
  
            const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity} = res.data;
  
            const articleOptions = refEnumItems2.current?.filter(x => x.enumerationCode === Enum_ARTICLE_OPTION &&
                                    x.parentEnumerationItemCode === filterOption);
            const articleOption = (articleOptions && articleOptions.length === 1) ? articleOptions[0].code || '' : '';            
            
            (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'article', productId: id,
                 productOptions, productOptionId: productOptions.find(opt => opt.isAvailable)!.id,
                 productName: name, lineOfBusinessCode, articleFilterOption: filterOption, articleOption } as unknown as IBillingDetail);   
          }
          
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenArticleFilter(false);
      }
  );

  const [openPackagingFilter, setOpenPackagingFilter] = useState(false);
  const basicFilterPackaging = useBasicFilterProductWithOptions( 3, refShowroomId.current, refCurrencyCode.current, 
    async (event: React.MouseEvent<unknown>, row: IProduct) => {
        const {id, description,name, serviceDurationInMinute, isActive, lineOfBusinessCode} = row;

        if(!isActive) {
          enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
          return;
        }

        setIsWaitingBoxShow(true);
        const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail},
                        currencyCode: watchCurrencyCode, storeId: watchShowroomId});
        setIsWaitingBoxShow(false);

        const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingPackagingDetails} = res.data;

        console.log(billingPackagingDetails);
        
        const countServices = billingPackagingDetails.filter(b => b.type === 'service').length;
        const countArticles = billingPackagingDetails.filter(b => b.type === 'article').length;

        for(var i=0; i< billingPackagingDetails.length; i++) {
          const billingPackagingDetail = billingPackagingDetails[i];
          if(billingPackagingDetail.type === 'article') continue;

          for(var j=0; j< billingPackagingDetail.billingServiceTasks.length; j++) {
            billingPackagingDetail.beneficiaryIsCustomer = true;
            const billingServiceTask = billingPackagingDetail.billingServiceTasks[j];
            if( employeeId > 0 && taskCodes.includes(billingServiceTask.taskCode)) {
              billingServiceTask.employeeId = employeeId;
              billingServiceTask.employeeFullName = `${userDescription} - ${userName}`;
            }
          }
        }
      
        const countBillingDetail = getValues().billingDetails.length;
        (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'packaging', productId: id, 
          productName: name, lineOfBusinessCode,
          billingPackagingDetails } as unknown as IBillingDetail);                          
          
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenPackagingFilter(false);
    }
  );

  const [openFormationFilter, setOpenFormationFilter] = useState(false);
  const basicFilterFormation = useBasicFilterProductWithOptions( 4, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive, lineOfBusinessCode} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail},
                                currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);

          const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingServiceTasks} = res.data;

          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'formation', productId: id,  
            productName: name, lineOfBusinessCode,
            beneficiaryIsCustomer: true, birthDateType: 'date' } as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenFormationFilter(false);
      }
  );

  const [openMaintenanceFilter, setOpenMaintenanceFilter] = useState(false);
  const basicFilterMaintenance = useBasicFilterProductWithOptions( 5, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive, lineOfBusinessCode, maintenanceCategoryClass} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail},
                      currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);

          const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingServiceTasks} = res.data;

          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'maintenance', productId: id,  
            productName: name, lineOfBusinessCode, maintenanceCategoryClass,
            beneficiaryIsCustomer: true, birthDateType: 'date' } as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenMaintenanceFilter(false);
      }
  );

  const [openContractFilter, setOpenContractFilter] = useState(false);
  const basicFilterContract = useBasicFilterProductWithOptions( 6, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, serviceDurationInMinute, isActive, lineOfBusinessCode, contractScopeClass} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail},
                      currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);

          const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity, billingServiceTasks} = res.data;

          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'contract', productId: id,  
            productName: name, lineOfBusinessCode, contractScopeClass,
            beneficiaryIsCustomer: true, birthDateType: 'date' } as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenContractFilter(false);
      }
  );

  const [openRentalFilter, setOpenRentalFilter] = useState(false);
  const basicFilterRental = useBasicFilterProductWithOptions( 8, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, description,name, lineOfBusinessCode, isActive, rentalPeriodicity, productOptions} = row;

          if(!isActive) {
            enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
            return;
          }

          const eff = new Date();
          const exp = addDurationInPeriod(eff, rentalPeriodicity, 1);

          setIsWaitingBoxShow(true);
          const res = await evaluateAddedProduct({...getValues(), productId: id, 
              billingDetail: {...defaultBillingDetail, rentalEffectiveDate: eff, rentalDuration: 1, rentalExpirationDate: exp},
                    currencyCode: watchCurrencyCode, storeId: watchShowroomId});
          setIsWaitingBoxShow(false);
          
          const countBillingDetail = getValues().billingDetails.length;
          (refAppendBillingDetails.current??emptyFunc)({...res.data,type: 'rental', productId: id,  
            rentalPeriodicity, rentalEffectiveDate: eff, rentalExpirationDate: exp, 
            productName: name, lineOfBusinessCode, rentalReference: '',
            productOptions,
            beneficiaryIsCustomer: true, birthDateType: 'date' } as unknown as IBillingDetail);                          
                      
          setTotalAmount();
          setProductIndex(countBillingDetail);
          setOpenRentalFilter(false);
      }
  );  

  const [openConsumptionFilter, setOpenConsumptionFilter] = useState(false);
  const basicFilterConsumption = useBasicFilterProductWithOptions( 9, refShowroomId.current, refCurrencyCode.current, 
      async (event: React.MouseEvent<unknown>, row: IProduct) => {
        const {id, description,name, serviceDurationInMinute, isActive, 
            consumptionOptionClass, lineOfBusinessCode, productOptions} = row;

        if(!isActive) {
          enqueueSnackbar( `${t('This product is not active')} : ${name}`, { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });
          return;
        }
          
        setIsWaitingBoxShow(true);
        const res = await evaluateAddedProduct({...getValues(), productId: id, billingDetail: {...defaultBillingDetail, quantity: 1},
                  currencyCode: watchCurrencyCode, storeId: watchShowroomId});
        setIsWaitingBoxShow(false);

        const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity} = res.data;
        
        const countBillingDetail = getValues().billingDetails.length;
        (refAppendBillingDetails.current??emptyFunc)({...res.data, type: 'consumption', productId: id,
          productOptions,
          productName: name, lineOfBusinessCode, consumptionOptionClass } as unknown as IBillingDetail); 

        setTotalAmount();
        setProductIndex(countBillingDetail);
        setOpenConsumptionFilter(false);
      }
  );
  

  const [openEmployeeFilter, setOpenEmployeeFilter] = useState(false);
  const basicFilterEmployee = useBasicFilterEmployee( 
      (event: React.MouseEvent<unknown>, row: IEmployee) => {
        const {id, firstName, lastName, managerFirstName, managerLastName, employeeTasks, } = row;        
        
        if(currentEmployeeUsage === 'business') {

          setValue('businessEmployeeId', id);
          setValue('businessEmployeeFullName', `${lastName} ${firstName}`.trim());
          const mFirstName = isFalsy(managerFirstName)?'':managerFirstName;
          const mLastName = isFalsy(managerLastName)?'':managerLastName;

          setValue('businessEmployeeManagerFullName', `${mLastName} ${mFirstName}`.trim());
          setOpenEmployeeFilter(false);
          return;
        }

        const billingDetail = getValues().billingDetails.at(productIndex);        
        if(isFalsy(billingDetail)) return;

                  
        // (refUpdateBillingDetail.current??emptyFunc)(productIndex, 
        //     {...service, employeeId: id, employeeFirstName: firstName, employeeLastName: lastName,
        //       employeeFullName: `${lg.includes('fr')? lastName:firstName} ${lg.includes('fr')? firstName:lastName}`.trim() });  

        setOpenEmployeeFilter(false);
      }
  );
    
  const [commissionDistributions, setCommissionDistributions] = useState<ICommissionDistribution[]>([]);

  const methods = useForm<IBilling>({defaultValues: {...defaultBilling, 
            currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate,
            showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0  }});
  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchId = watch('id');
  const watchStatus = watch('status');
  const watchHasNumber = watch('hasNumber');

  const watchIsProforma = watch('isProforma');

  const watchDefaultTaxRate = watch('defaultTaxRate');

  const watchCustomerId = watch('customerId');
  const watchCustomerPersonType = watch('customer.type');
  const watchBusinessEmployeeId = watch('businessEmployeeId');  

  const watchCustomerFirstName = watch('customer.firstName');
  const watchCustomerLastName = watch('customer.lastName');
  const watchCustomerCorporationName = watch('customer.corporationName');
  const watchCustomerBirthDateType = watch('customer.birthDateType');
  const watchCustomerCreationDateType = watch('customer.creationDateType');

  const watchDefaultPaymentSourceCode = watch('defaultPaymentSourceCode');
  const watchTermAndConditionCode = watch('termAndConditionCode');

  const watchShowroomId = watch('showroomId');
  const watchCurrencyCode = watch('currencyCode');
  

  const methodsEntityAction = useForm<IFeatureDescription>( {defaultValues:defaultFeatureDescription} );
    const { register: registrationEntityAction, setValue: setEntityActionValue ,getValues: getEntityActionValues ,
            watch: watchEntityAction, reset: resetEntityAction ,control: controlEntityAction , formState: { errors: errorsEntityAction } } = methodsEntityAction;
    let {fields: actionParams} = useFieldArray<IFeatureDescription,'params'>({
    control: controlEntityAction, 
    name: 'params'});

  const { register: registerBillingDelivery, setValue: setValueBillingDelivery ,getValues: getValuesBillingDelivery, watch: watchBillingDelivery, 
    reset: resetBillingDelivery ,control: controlBillingDelivery , formState: { errors: errorsBillingDelivery } } = 
          useForm<IBillingDelivery>({defaultValues: defaultBillingDelivery });


  type SearchPersonUsage = 'customer' | 'billing-detail-beneficiary' | 'billing-packaging-beneficiary';
  const [currentSearchPersonUsage, setCurrentSearchPersonUsage] = useState<SearchPersonUsage>('customer');

  const refProductIndex = useRef<number>(0);
  const refBillingPackagingServiceIndex = useRef<number>(0);

  const refDefaultPaymentSourceCode = useRef<string>('');
  const refTermAndCondition = useRef<string>('');

  const [openPersonSearchFilter, setOpenPersonSearchFilter] = useState(false);
  const basicFilterPersonSearch = useBasicFilterPersonSearch( 
      () => {
        const { firstName, lastName, corporationName, type } = getValues().customer;

        const fn = type === 'moral' ? corporationName : firstName;
        const ln = type === 'moral' ? corporationName : lastName;

        if(currentSearchPersonUsage === 'customer')
          return {firstName: fn, lastName: ln, particularSign: ''}; 

        if(currentSearchPersonUsage === 'billing-detail-beneficiary') {
          const billingDetail = getValues().billingDetails.at(refProductIndex.current);
          if(isFalsy(billingDetail))
            return {firstName: '@@@@', lastName: '@@@@', particularSign: '@@@@'}; 

          return {firstName: billingDetail.firstName??'', lastName: billingDetail.lastName??'', particularSign: ''};
        }

        if(currentSearchPersonUsage === 'billing-packaging-beneficiary') {
          const billingDetail = getValues().billingDetails.at(refProductIndex.current);
          
          if(isFalsy(billingDetail))
            return {firstName: '@@@@', lastName: '@@@@', particularSign: '@@@@'}; 

          const billingPackagingService = billingDetail.billingPackagingDetails.at(refBillingPackagingServiceIndex.current);          
          if(isFalsy(billingPackagingService))
            return {firstName: '@@@@', lastName: '@@@@', particularSign: '@@@@'}; 

          return {firstName: billingPackagingService.firstName??'', lastName: billingPackagingService.lastName??'', particularSign: ''};
        }

        
        return {firstName: firstName, lastName: lastName, particularSign: ''};
        //return refPersonSearch.current || {firstName: '', lastName: '', particularSign: ''};
      },      
      (event: React.MouseEvent<unknown>, row: IPerson) => {
          const {id, type, firstName, lastName, birthDate, birthPlace, particularSign, corporationName, corporationType,
            portable1, portable2, email1, email2} = row;
        
        
        if(currentSearchPersonUsage === 'customer') {
          
          setValue('customerId', id);
          setValue('customer', row);
          // setValue('customerPersonType', type);
          // setValue('customerFirstName', firstName);
          // setValue('customerLastName', lastName);  
          // setValue('customerCorporationName', corporationName);
          // setValue('customerCorporationType', corporationType);
          // setValue('customerPortable1', portable1);   
          // setValue('customerPortable2', portable2);                    
          // setValue('customerEmail1', email1); 
          // setValue('customerEmail2', email2); 
        }           
                          
        setOpenPersonSearchFilter(false);
      } 
  );
  

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IBilling>,Error,IBilling>(
      _id>0?updateBilling:createBilling, {   
        onSuccess: (data: IResult<IBilling>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
          
          
          setIsSaveLoading(false);
          //setValue('id', data.data.id);
          _setId(data.data.id);
          //retrieveData('Billing',data.data.id, refetch);
          setCurrentFormNameAtom(`${t('Billing')} - # ${data.data.id} # ` );
          
          //queryClient.invalidateQueries(['Billing',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });


      const { mutate: mutateDeliverBillingDetail } = useMutation<IResult<IBillingDetail>,Error, {id: number, deliveryDate: Date, isDelivered: boolean, isDeliveryDateDefine: boolean } >(
          deliverBillingDetail , {   
          onSuccess: (data: IResult<IBillingDetail>) => {
            enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                  anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                        
            const index = getValues().billingDetails.findIndex(b => b.id === data.data.id);
            if(index >= 0) {
              const billingDetail = getValues().billingDetails[index];

              (refUpdateBillingDetail.current??emptyFunc)(index, {...billingDetail, 
                      isDelivered: data.data.isDelivered, isDeliveryDateDefine: data.data.isDeliveryDateDefine , 
                      deliveryDate: data.data.deliveryDate} as unknown as IBillingDetail);
            }
            
            setOpenBillingDetailServiceDelivery(false);
          },
          onError: (err: Error) => {          
            enqueueSnackbar( err?.message, { variant: 'error',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
            setOpenBillingDetailServiceDelivery(false);      
          }
        });

      const { mutate: mutateEntityFeature } = useMutation<any,Error,IFeatureDescription>(executeEntityFeature, {        
        onSuccess: (data: any) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 });  
        
                // if add enum item, reload items...   
                refetchEnumerationItems();
        },
        onError: (err: Error) => {
          
          if(error && error.message)
            enqueueSnackbar( `!! ${error?.message}`, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
        }
      });

      
    const {data: _data, refetch} = useQuery<IBilling>(['Billing', _id], () => retrieveEntity('Billing',_id, 'Full'), 
      {refetchOnWindowFocus: false ,enabled: false } );

    const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>( ['EnumerationItems', 'Billing'], () => getEnumerationItemsByEnumerationCodes
      ( [Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE, Enum_BILLING_TERM_REFERENCE,Enum_PAYMENT_SOURCE,
          Enum_PRICE_PURPOSE, Enum_CURRENCY, Enum_CORPORATION_TYPE ] ));
   
    const {data: enumItems2} = useQuery<IEnumerationItem[]>( ['EnumerationItems2', 'Billing'], () => getEnumerationItemsByEnumerationCodes
      ( [Enum_SERVICE_TASK, 
          Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_CONSUMPTION_OPTION,
          Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_RENTAL_PERIODICITY  ] ));

    const {data: store} = useQuery<IStore>( ['Billing', 'Store', getValues().showroomId], () => 
      retrieveEntity('Store',getValues().showroomId), {refetchOnWindowFocus: false ,enabled: getValues().showroomId > 0 } );

          
    type PersonUsage = 'customer' | 'billing-detail-service' | 'billing-packaging-service' | 'billing-detail-formation';

    const [currentPersonUsage, setCurrentPersonUsage] = useState<PersonUsage>('customer');
    const handleClickOpenPerson = async (event: any, usage: PersonUsage) => {
      setCurrentPersonUsage(usage);
      setOpenPersonFilter(true);
    }

    const handleClickRemovePerson = async (event: any, usage: PersonUsage) => {

      if(usage === 'customer') {
        (setValue as any)('customerId', 0);
        setValue('customer', defaultPerson);
        // setValue('customerFirstName', '');
        // setValue('customerLastName', '');
        // setValue('customerCorporationName', '');
        // setValue('customerCorporationType', '');
        // setValue('customerPortable1', '');
        // setValue('customerPortable2', '');
        // setValue('customerEmail1', '');
        // setValue('customerEmail2', '');

      }       
    }

    const [personsCopy, setPersonsCopy] = useState<IPerson[]>([]);
    const [openPersonCopy, setOpenPersonCopy] = useState<boolean>(false);
    const handleClickOpenPersonCopy = async (event: any, usage: PersonUsage) => {
      setCurrentPersonUsage(usage);

      var persons : IPerson[] = [];
      getValues().billingDetails.forEach( billingDetail => {
          if( ['service', 'formation'].includes(billingDetail.type) && billingDetail.personId <= 0 && 
                isFalsy(persons.find( p => p.firstName === billingDetail.firstName && p.lastName === billingDetail.lastName ) ) )
            persons.push( {...defaultPerson, id: -persons.length, firstName: billingDetail.firstName, lastName: billingDetail.lastName} );
          else if(billingDetail.type === 'packaging') {
            billingDetail.billingPackagingDetails.forEach( billingPackagingDetail => {
              if(billingPackagingDetail.type === 'service' && billingPackagingDetail.personId <= 0 && 
                  isFalsy(persons.find( p => p.firstName === billingPackagingDetail.firstName && p.lastName === billingPackagingDetail.lastName ) ) )
                persons.push( {...defaultPerson, id: -persons.length, firstName: billingPackagingDetail.firstName, lastName: billingPackagingDetail.lastName} );
            })
          }
        });

      setPersonsCopy(persons);
      setOpenPersonCopy(true);
    }

    const handlePersonCopyDoubleClick = (event: React.MouseEvent<unknown>, row: IPerson) => {
      const {firstName, lastName, portable1, portable2, email1, email2} = row;

      
      setOpenPersonCopy(false);
    }
    

    type EmployeeUsage = 'business' | 'billing-detail-service' | 'billing-packaging-service' | 'billing-service' | 'billing-packaging' | 'fee';
    const [currentEmployeeUsage, setCurrentEmployeeUsage] = useState<EmployeeUsage>('billing-detail-service');

    const handleClickSelectEmployee = (event: any, employeeUsage: EmployeeUsage) => {
      setCurrentEmployeeUsage(employeeUsage);      
      setOpenEmployeeFilter(true);
    }

    const handleClickResetEmployee = (event: any, employeeUsage: EmployeeUsage) => {
      
        if(employeeUsage === 'business') {
          (setValue as any)('businessEmployeeId', 0);
          (setValue as any)('businessEmployeeFullName', '');
          (setValue as any)('businessEmployeeManagerFullName', '');
        } 
    }


    const [openCommissionDistribution, setOpenCommissionDistribution] = useState<boolean>(false);
    const handleClickCommissionDistribution = async (event: any) => {

      const arr = await getCommissionDistributionsByBilling(getValues().id);
      setCommissionDistributions(arr);
      setOpenCommissionDistribution(true);
    }

    const [productIndex, setProductIndex] = useState<number>(-1);
        
    const getArticleOptionBillingDetailList = (row: IBillingDetail, cellId: keyof IBillingDetail, 
      opts: {value: string, name: string}[]) => {        
      
      const {articleFilterOption} = row;
      const enumsFilter = (refEnumItems.current ?? []).filter(x => x.parentEnumerationItemCode === articleFilterOption);
    
      return getAsOptions(enumsFilter ,Enum_ARTICLE_OPTION);
    }

    const cellEditableBillingDetail = (row: IBillingDetail, cellId: keyof IBillingDetail) => { 
      
      // if(row.type === 'article' && cellId === 'quantity') return true;
      // if(row.type === 'article' && cellId === 'articleOption') return true;

      return true;
    }

    const cellAllowedDiscount = (row: IBillingDetail, valueAmount: number) => {
      const {type, maxDiscountAmount, loaded, baseAmount, unityAmount} = row;      
      
      if( (valueAmount<0) || (valueAmount>0 && loaded>0) ) {
        enqueueSnackbar( `${t('Invalid amount')} ${valueAmount} : [0-${maxDiscountAmount}]` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      if( valueAmount > 0 && maxDiscountAmount<valueAmount) {
        enqueueSnackbar( `${t('The maximum discount amount is')} : ${maxDiscountAmount}` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      if(['article','packaging','consumption', 'rental'].includes(type) && valueAmount > unityAmount){
        enqueueSnackbar( `${t('The maximum discount amount is')} : ${unityAmount}` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      if( !['article','packaging','consumption', 'rental'].includes(type) && valueAmount > baseAmount){
        enqueueSnackbar( `${t('The maximum discount amount is')} : ${baseAmount}` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      debouncedAmount();
      return true;
    }

    const cellAllowedLoaded = (row: IBillingDetail, valueAmount: number) => {
      const {maxLoadedAmount, discount} = row;      

      if((valueAmount<0) || (valueAmount>0 && discount > 0) ) {
        enqueueSnackbar( `${t('Invalid amount')} : [0-${maxLoadedAmount}]` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      if( valueAmount > 0 && maxLoadedAmount<valueAmount) {
        enqueueSnackbar( `${t('The maximum loaded amount is')} : ${maxLoadedAmount}` ,
           { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return false;
      }

      debouncedAmount();
      return true;
    }

    const debouncedAmount = useRef(
      debounce( () => {
        const index = refProductIndex.current;

          const billingDetail = getValues().billingDetails.at(index); 
          if(isFalsy(billingDetail)) return; 

          const {type, quantity, rentalDuration ,discount, loaded, baseAmount, 
              netAmount, tax ,unityAmount, isTaxable, isDefaultTaxFormula} = billingDetail!;

          const qtyFactor = ['article','packaging','consumption'].includes(type) ? quantity : rentalDuration;
          
          const newNetAmount = ['article','packaging','consumption', 'rental'].includes(type) ? (unityAmount-discount+loaded)*qtyFactor : baseAmount-discount+loaded;
          const newBaseAmount = ['article','packaging','consumption', 'rental'].includes(type) ? unityAmount*qtyFactor : baseAmount;

          if(newNetAmount < 0)  {
            enqueueSnackbar( `${t('Invalid data !!!')} : ${newNetAmount}` ,
                { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
            return;
          }

          let newTax = isTaxable? (getValues().defaultTaxRate*newNetAmount)/100 : 0;
          if(!isDefaultTaxFormula) {
            // TO DO: Evaluate base on expression of tax for the product.
          } 

          (refUpdateBillingDetail.current??emptyFunc)(index, {...billingDetail!, 
              baseAmount: newBaseAmount, netAmount: newNetAmount, 
              tax: newTax, amount: newNetAmount+newTax  } as unknown as IBillingDetail );  
          setTotalAmount();
      }, 700)
    ).current;

    const [headBillingDetailCells, setHeadBillingDetailCells]  = useState<HeadCell<IBillingDetail>[]>([]);
    useEffect(() => {
      setHeadBillingDetailCells([   
        {id:'type', label : t('Type'),  display: false, type: 'string', width: 15},   
        {id:'productName', label : t('Product'),  display: true, type: 'string', width: 40}, 
  
        {id:'baseAmount', label : t('Base'),  display: true, type: 'numeric', width: 12, decimalScale: 2, }, 
        {id:'discount', label : t('Discount'),  display: true, type: 'numeric', width: 12, 
          isEditable: cellEditableBillingDetail, isAllowed: cellAllowedDiscount, decimalScale: 2}, 
        {id:'loaded', label : t('Loaded'),  display: true, type: 'numeric', width: 12, decimalScale: 2, 
          isEditable: cellEditableBillingDetail, isAllowed: cellAllowedLoaded}, 
        {id:'isDelivered', label : t('Done ?'),  display: true, type: 'boolean', width: 12}, 
        
        
        {id:'amount', label : t('Amount'),  display: true, type: 'numeric', width: 12, decimalScale: 2 },           
      ]);
    }, [t,i18n]);
        

    const refAppendBillingDetails = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
    const refUpdateBillingDetail = useRef<(index: number,value: Partial<FieldArray<IBilling>> ) => void>(null);
    const refRemoveBillingDetail = useRef<(index: number ) => void>(null);
      
    const handleBillingDetailSelected = (event: React.MouseEvent<unknown>,index: number,row: IBillingDetail) => {
      billingDetailSelected(index);
    }

    const billingDetailSelected = async (index: number) => {
      
      setValue('currentBillingDetailIndex', index);
      //setProductIndex(index);
    }

    const cellEditableBillingInfo = (row: IBillingInfo, cellId: keyof IBillingInfo) => { 
      return true;
  }

  const [headBillingInfoCells, setHeadBillingInfoCells]  = useState<HeadCell<IBillingInfo>[]>([]);
  useEffect(() => {
    setHeadBillingInfoCells([   
      {id:'description', label : t('Description'),  display: true, type: 'string', width: 60, isEditable: cellEditableBillingInfo},   
      {id:'designation', label : t('Designation'),  display: true, type: 'string', width: 40, isEditable: cellEditableBillingInfo}, 
    ]);
  }, [t,i18n]);
      

  const refAppendBillingInfos = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateBillingInfo = useRef<(index: number,value: Partial<FieldArray<IBilling>> ) => void>(null);
  const refRemoveBillingInfo = useRef<(index: number ) => void>(null);
  
  const billingInfoRowActionIcon = ( row: IBillingInfo) : ActionIconTableRow<IBilling,IBillingInfo> => {
      
    
    const res: ActionIconTableRow<IBilling,IBillingInfo> = {
      toolTip: 'viewDetails',
      icon: RemoveCircleIcon,
      hasAction: (getValues().status === '95'),  // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event : any,index: number, row: IBillingInfo) => {
        
        (refRemoveBillingInfo.current??emptyFunc)(index);                        
      }
    }
    return res;
}

    const cellEditableBillingDetailInfo = (row: IBillingDetailInfo, cellId: keyof IBillingDetailInfo) => { 
        return true;
    }

    const [headBillingDetailInfoCells, setHeadBillingDetailInfoCells]  = useState<HeadCell<IBillingDetailInfo>[]>([]);
    useEffect(() => {
      setHeadBillingDetailInfoCells([   
        {id:'description', label : t('Description'),  display: true, type: 'string', width: 60, isEditable: cellEditableBillingDetailInfo},   
        {id:'designation', label : t('Designation'),  display: true, type: 'string', width: 40, isEditable: cellEditableBillingDetailInfo}, 
      ]);
    }, [t,i18n]);
        

    const refAppendBillingDetailInfos = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
    const refUpdateBillingDetailInfo = useRef<(index: number,value: Partial<FieldArray<IBilling>> ) => void>(null);
    const refRemoveBillingDetailInfo = useRef<(index: number ) => void>(null);
     
    const billingDetailInfoRowActionIcon = ( row: IBillingDetailInfo) : ActionIconTableRow<IBilling,IBillingDetailInfo> => {
      
    
      const res: ActionIconTableRow<IBilling,IBillingDetailInfo> = {
        toolTip: 'viewDetails',
        icon: RemoveCircleIcon,
        hasAction: (getValues().status === '95'),  // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
        isActionExecuting: true,
        onRowClickIcon: (event : any,index: number, row: IBillingDetailInfo) => {
          
          (refRemoveBillingDetailInfo.current??emptyFunc)(index);                        
        }
      }
      return res;
  }

    const [openBillingDetailDuplicateDialog, setOpenBillingDetailDuplicateDialog] = useState<boolean>(false);
    const handleBillingDetailRowDoubleClick = (event: React.MouseEvent<unknown>,index: number,row: IBillingDetail) => {
      billingDetailSelected(index);
      const billingDetail = getValues().billingDetails.at(index);
      if(isFalsy(billingDetail)) return;

      if(!['service', 'package'].includes(billingDetail.type)) return;

      setOpenBillingDetailDuplicateDialog(true);
    }

    const handleOkBillingDetailDuplicate = (event: any) => {

      const billingDetail = getValues().billingDetails.at(productIndex);
      if(isFalsy(billingDetail)) return;
      
      if(!['service', 'package'].includes(billingDetail.type)) return;

      const {serviceDuplicateCount } = getValues(); 
      if(serviceDuplicateCount < 2 ) return;

      (refAppendBillingDetails.current??emptyFunc)( range(0, serviceDuplicateCount - 1).map(i => ({...billingDetail})) ); 

      setOpenBillingDetailDuplicateDialog(false);
    }
    

    const handleAddServices = (event: any) => { 
      refProductFilter.current = 1; // service     
      setOpenServiceFilter(true);
    }

    const handleAddFees = (event: any) => {      
      setOpenFeeFilter(true);
    }
    
    const handleAddArticles = (event: any) => {
      refProductFilter.current = 2; // article
      //(refAppendBillingDetails.current??emptyFunc)({id: 0, resourceTypeId: _id, taskCode: '', part: 0,  });
      setOpenArticleFilter(true);
    }

    const handleAddPackagings = (event: any) => {
      //(refAppendBillingDetails.current??emptyFunc)({id: 0, resourceTypeId: _id, taskCode: '', part: 0,  });
      setOpenPackagingFilter(true);
    }

    const handleAddFormations = (event: any) => {      
      setOpenFormationFilter(true);
    }

    const handleAddMaintenances = (event: any) => {      
      setOpenMaintenanceFilter(true);
    }

    const handleAddContracts = (event: any) => {      
      setOpenContractFilter(true);
    }
    
    const handleAddRentals = (event: any) => {      
      setOpenRentalFilter(true);
    }

    const handleAddConsumptions = (event: any) => {      
      setOpenConsumptionFilter(true);
    }

    const handleAddLaundries = (event: any) => {     
      refProductFilter.current = 11; // service  
      setOpenProductFilter(true);
    }

    const handleAddExams = (event: any) => {   
      refProductFilter.current = 12; // service    
      setOpenProductFilter(true);
    }

    

    const handleAddBillingInfos = (event: any) => {      
      if(getValues().proxy.billingInfos.some( x => isFalsy(x.description) && isFalsy(x.designation))) return;

      (refAppendBillingInfos.current??emptyFunc)({id: 0, billingId: 0, description: '', designation: '' });   
    }

    const handleAddBillingDetailInfos = (event: any) => {      
      const billingDetail = getValues().billingDetails.at(productIndex);
      if(isFalsy(billingDetail)) return;

      if(billingDetail.billingDetailInfos.some( x => isFalsy(x.description) && isFalsy(x.designation))) return;

      (refAppendBillingDetailInfos.current??emptyFunc)({id: 0, billingDetailId: billingDetail.id, description: '', designation: '' });   
    }
    // const handleAddNewRessources = (event: any) => {
    //   (refAppendBillingDetails.current??emptyFunc)({id: 0, maintenanceId: _id, resourceId: 0,
    //     resourceDescription: '', resourceTypeId: 0, resourceTypeName: '',
    //     maintenanceReference: '', maintenanceDescription: '', maintenanceSupplierFullName: '',
    //     description: '', amount: 0,  });
    // }
    const  [openBillingDetailServiceDelivery, setOpenBillingDetailServiceDelivery] = useState<boolean>(false);

    const billingDetailRowActionIcon = ( row: IBillingDetail) : ActionIconTableRow<IBilling,IBillingDetail> => {
      
      const roleEntityBilling = roleEntities.find(re => re.entityName === 'Billing');

      const canDeliverService = !isFalsy(roleEntityBilling) && 
                          roleEntityBilling.roleEntityFeatures.some(f => f.featureName === 'DeliverBillingDetailBillingHandler');

      const res: ActionIconTableRow<IBilling,IBillingDetail> = {
        toolTip: 'viewDetails',
        icon: getValues().status === '95'? RemoveCircleIcon : CheckIcon,
        hasAction: (getValues().status === '95') || (getValues().status === '10' && !row.isDelivered && canDeliverService ),  // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
        isActionExecuting: true,
        onRowClickIcon: (event : any,index: number, row: IBillingDetail) => {
          
          setProductIndex(index);

          if(getValues().status === '95') {
            if(index>0)
              billingDetailSelected(index-1);  
            else if(getValues().billingDetails.length>1)
              billingDetailSelected(index+1);  
            else billingDetailSelected(index-1); 
          
            (refRemoveBillingDetail.current??emptyFunc)(index);  
            
            setTotalAmount(); 
          } else if(getValues().status === '10') {
            setOpenBillingDetailServiceDelivery(true);
          }                      
        }
      }
      return res;
  }

  

  const handleOkBillingDetailServiceDelivery = async () => {

    if(productIndex < 0 || productIndex >= getValues().billingDetails.length ) return;

    const billingDetail = getValues().billingDetails.at(productIndex);
    if(isFalsy(billingDetail)) return;

    const {id, deliveryDate, isDelivered, isDeliveryDateDefine} = billingDetail;
    mutateDeliverBillingDetail({id, deliveryDate, isDelivered, isDeliveryDateDefine});
  }

  const handleCustomerType = ( event: React.MouseEvent<HTMLElement>, newCustomerType: PersonType ) => {
  
    if(newCustomerType === null) return;
    (setValue as any)('customer.type', newCustomerType);
  };

  useEffect(() => {
    
      const subscription = watch( async (value, { name, type }) => {
        
        if( name?.startsWith('billingDetails') && !name?.includes('billingPackagingDetails') && (name?.endsWith('firstName') || name?.endsWith('lastName')) ) {
            
          setCurrentSearchPersonUsage('billing-detail-beneficiary');         
          debouncedDetailPersonChange();                          
        } else if(name?.startsWith('billingDetails') && name?.includes('billingPackagingDetails') && (name?.endsWith('firstName') || name?.endsWith('lastName'))) {

          setCurrentSearchPersonUsage('billing-packaging-beneficiary');         
          debouncedPackagingPersonChange();
        } else if(name?.startsWith('billingDetails') && name?.endsWith('quantity')) {
          debouncedQuantityChange();
        } else if(name?.startsWith('billingDetails') && (name?.endsWith('rentalDuration') || name?.endsWith('rentalEffectiveDate')  )) {
          debouncedRentalDurationChange();
        }
      });  
  
      return () => subscription.unsubscribe();
    }, [watch]);


  const setTotalAmount = () => {
    const sumNetAmount = sum( getValues().billingDetails.map( ({netAmount}) => netAmount ) );
    const sumTaxAmount = sum( getValues().billingDetails.map( ({tax}) => tax ) );
    //const sumAmount = sum( getValues().billingDetails.map( ({amount}) => amount ) );

    const net = sumNetAmount - getValues().discount;
    const tax = sumNetAmount === 0 ? 0 : (net * sumTaxAmount) / sumNetAmount;

    const amount = net + tax;

    setValue('initialNetAmount', sumNetAmount);
    setValue('netAmount', net);
    setValue('tax', tax);
    setValue('amount', amount);
    setValue('unpaidAmount', amount - getValues().amountPaid);

  }

  
  const [openBillingInfo, setOpenBillingInfo] = useState<boolean>(false);
  const handleLinkClickBillingInfo = (event: any) => {
    setOpenBillingInfo(true);
  }
  

  const [currentCrudAction, setCurrentCrudAction] = useState<'C'|'U'>('C');
  const [openActionParams, setOpenActionParams] = useState<boolean>(false);
  const [actionParamsDialogTitle, setActionParamsDialogTitle] = useState<string>('');

  // const [canAddEnumerationItem, setCanAddEnumerationItem] = useState<boolean>(
  //   roleEntities.some(e => e.entityName === 'Enumeration') && 
  //   (roleEntities.find(e => e.entityName === 'Enumeration')?.roleEntityFeatures || []).some(f => 
  //             f.featureName === 'AddBasicEnumerationWithEnumerationItemEnumerationHandler') );

  // const [canUpdateEnumerationItem, setCanUpdateEnumerationItem] = useState<boolean>(
  //   roleEntities.some(e => e.entityName === 'Enumeration') && 
  //   (roleEntities.find(e => e.entityName === 'Enumeration')?.roleEntityFeatures || []).some(f => 
  //             f.featureName === 'UpdateBasicEnumerationItemEnumerationHandler') );


  //   const handleAddEnumerationItemClick = (crudAction: 'C'|'U', 
  //             enumerationCode: string, enumerationName: string, enumerationDescription: string,
  //             enumerationItemCode: string, enumerationItemName: string, enumerationItemDescription: string) => {
    
  //   setCurrentCrudAction(crudAction);
  //   resetEntityAction({...defaultFeatureDescription, 
  //       name: crudAction === 'C' ? 
  //               'AddBasicEnumerationWithEnumerationItemEnumerationHandler' : 
  //               'UpdateBasicEnumerationItemEnumerationHandler', 
  //       label: '', 
  //       inputType: 0, type: 2,

  //       entityName: 'Enumeration', entityId: 0, entityItemName: '',
  //       // 
  //       params: [
  //         {name: 'Code', label: `${t('Codification')} - ${t('Code')} `, isReadonly: true, dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationCode, dateValue: new Date()},
  //         {name: 'Name', label: `${t('Codification')} - ${t('Name')} `, isReadonly: true, dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationName, dateValue: new Date()},
  //         {name: 'Description', label: `${t('Codification')} - ${t('Description')} `, isReadonly: true, dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationDescription, dateValue: new Date()},

  //         {name: 'EnumerationItemCode', label: t('Code'), dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationItemCode, dateValue: new Date()},
  //         {name: 'EnumerationItemName', label: t('Name'), dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationItemName, dateValue: new Date()},
  //         {name: 'EnumerationItemDescription', label: t('Description'), dataType: 'Base', type: 'string', enumerationCode: '', value: enumerationDescription, dateValue: new Date()},
  //         // {name: 'Code', label: 'Code', dataType: 'Base', type: 'string', enumerationCode: '', value: '', dateValue: new Date()},
  //         // {name: 'Code', label: 'Code', dataType: 'Base', type: 'string', enumerationCode: '', value: '', dateValue: new Date()},
  //         // {name: 'Code', label: 'Code', dataType: 'Base', type: 'string', enumerationCode: '', value: '', dateValue: new Date()},
  //       ]
  //     });
  //   setActionParamsDialogTitle(`${t('New')} : ${enumerationName}`)
  //   setOpenActionParams(true);
  // }

  const handleOkActionParams = () => {   
    setOpenActionParams(false);
    
    const actionSelected = getEntityActionValues(); 
    mutateEntityFeature( actionSelected );
}
  

  const debouncedQuantityChange = useRef(
    debounce( async () => {      
      
      const index = refProductIndex.current;

      const billingDetail = getValues().billingDetails.at(index);
      if(isFalsy(billingDetail)) return;
      
      const {type, quantity,  rentalDuration, productOptionId , isTaxable, isDefaultTaxFormula,discount, loaded, 
                    baseAmount, unityAmount, isPriceDependsOnQuantity} = billingDetail!;
      if(isPriceDependsOnQuantity) {
        alert('price depends on quantity'); // TO DO:
      }
    
      const qtyFactor = ['article','packaging','consumption'].includes(type) ? quantity : rentalDuration;

      const newNetAmount = ['article','packaging','consumption', 'rental'].includes(type) ? (unityAmount-discount+loaded)*qtyFactor : baseAmount-discount+loaded;
      const newBaseAmount = ['article','packaging','consumption', 'rental'].includes(type) ? unityAmount*qtyFactor : baseAmount;

      if(newNetAmount < 0)  {
        enqueueSnackbar( `${t('Invalid data !!!')} : ${newNetAmount}` ,
            { variant: 'warning', anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
        return;
      }

      let newTax = isTaxable ? (getValues().defaultTaxRate*newNetAmount)/100 : 0;
      if(!isDefaultTaxFormula) {
        // TO DO: Evaluate base on expression of tax for the product.
      }

      (refUpdateBillingDetail.current??emptyFunc)(index, {...billingDetail!, 
          baseAmount: newBaseAmount, netAmount: newNetAmount, 
          tax: newTax, amount: newNetAmount+newTax } as unknown as IBillingDetail);

      setTotalAmount();

    }, 1500)
  ).current;


  const debouncedRentalDurationChange = useRef(
    debounce( async () => {      
      
      const index = refProductIndex.current;

      const billingDetail = getValues().billingDetails.at(index);
      if(isFalsy(billingDetail)) return;
      
      const {type, rentalDuration, rentalPeriodicity, productId, isTaxable, isDefaultTaxFormula} = billingDetail!;
      
      setIsWaitingBoxShow(true);
      const res = await evaluateAddedProduct({...getValues(), productId, billingDetail,
                currencyCode: watchCurrencyCode, storeId: watchShowroomId});
      setIsWaitingBoxShow(false);

      const {baseAmount, discount, loaded, tax, unityAmount } = res.data;

      const newNetAmount = (unityAmount-discount+loaded)*rentalDuration;
      let newTax = isTaxable ? (getValues().defaultTaxRate*newNetAmount)/100 : 0;
      if(!isDefaultTaxFormula) {
        // TO DO: Evaluate base on expression of tax for the product.
      }

      const effectiveDate = billingDetail!.rentalEffectiveDate;
      const expirationDate = addDurationInPeriod(effectiveDate, rentalPeriodicity ,rentalDuration);      

      (refUpdateBillingDetail.current??emptyFunc)(index, {...billingDetail!, 
          rentalExpirationDate: expirationDate,
          baseAmount, netAmount: newNetAmount, tax: newTax, amount: newNetAmount+newTax } as unknown as IBillingDetail);

      setTotalAmount();

    }, 1500)
  ).current;

  const debouncedDefaultTaxRateChange = useRef(
    debounce( async () => {                  
      
      const {billingDetails, defaultTaxRate } = getValues();

      setValue('billingDetails', 
        billingDetails.map(b => ({...b, 
            tax:  b.isTaxable ? (b.netAmount*defaultTaxRate)/100 : 0, 
            amount: b.isTaxable ? (b.netAmount*(100+defaultTaxRate))/100 : b.netAmount }))
      );     
      
      setTotalAmount();
    }, 1500)
  ).current;
  

  const debouncedCustomerChange = useRef(
    debounce( async () => {      
      const { customerId } = getValues();

      const { firstName, lastName, corporationName, type } = getValues().customer;

      if(customerId>0) return;

      const fn = type === 'physical' ? firstName: corporationName;
      const ln = type === 'physical' ? lastName: corporationName;
      
      if( (isFalsy(fn) || fn === '') && (isFalsy(ln) || ln === '' )) return;

      const count = await getPersonsSearchCount(fn, ln, '');

      if(!isFalsy(refCustomerSnackbarId.current)) closeSnackbar(refCustomerSnackbarId.current!);
      
      if(count > 0) 
        refCustomerSnackbarId.current = enqueueSnackbar( `${t('Identity')} --> ${t('You have to check if person exists before save, verify it in this list of')} : ${count} ${t('_person')}(s)`, { variant: 'warning',
              anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, persist : true, //autoHideDuration : 5000,
              action: xActionPersonSearch('customer') } );             

    }, 1500)
  ).current;

  const debouncedDetailPersonChange = useRef(
    debounce( async () => {      

      const billingDetail = getValues().billingDetails.at(productIndex);
      if(isFalsy(billingDetail)) return;

      const { personId, firstName, lastName } = billingDetail!;

      if(personId>0) return;

      if( (isFalsy(firstName) || firstName === '') && (isFalsy(lastName) || lastName === '' )) return;

      const count = await getPersonsSearchCount(firstName??'', lastName??'', '');

      if(!isFalsy(refDetailBeneficiarySnackbarId.current)) closeSnackbar(refDetailBeneficiarySnackbarId.current!);
      
      if(count > 0) 
        refDetailBeneficiarySnackbarId.current = enqueueSnackbar( `${t('Identity')} --> ${t('You have to check if person exists before save, verify it in this list of')} : ${count} ${t('_person')}(s)`, { variant: 'warning',
              anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, persist : true, //autoHideDuration : 5000,
              action: xActionPersonSearch('billing-detail-beneficiary') } );    
    }, 1500)
  ).current;

  const debouncedPackagingPersonChange = useRef(
    debounce( async () => {      
      
      const billingDetail = getValues().billingDetails.at(refProductIndex.current);
      
      if(isFalsy(billingDetail)) return;

      const billingPackagingService = billingDetail.billingPackagingDetails.at(refBillingPackagingServiceIndex.current);
      
      if(isFalsy(billingPackagingService)) return;

      const { personId, firstName, lastName } = billingPackagingService!;

      if(personId>0) return;

      if( (isFalsy(firstName) || firstName === '') && (isFalsy(lastName) || lastName === '' )) return;

      const count = await getPersonsSearchCount(firstName??'', lastName??'', '');

      if(!isFalsy(refPackagingBeneficiarySnackbarId.current)) closeSnackbar(refPackagingBeneficiarySnackbarId.current!);
      
      if(count > 0) 
        refPackagingBeneficiarySnackbarId.current = enqueueSnackbar( `${t('Identity')} --> ${t('You have to check if person exists before save, verify it in this list of')} : ${count} ${t('_person')}(s)`, { variant: 'warning',
              anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, persist : true, //autoHideDuration : 5000,
              action: xActionPersonSearch('billing-packaging-beneficiary') } );    
    }, 1500)
  ).current;

  const refCustomerSnackbarId = useRef<SnackbarKey>();
  const refDetailBeneficiarySnackbarId = useRef<SnackbarKey>();
  const refPackagingBeneficiarySnackbarId = useRef<SnackbarKey>();

  const xActionPersonSearch = (searchPersonUsage: SearchPersonUsage): SnackbarAction => (snackbarId: SnackbarKey) => (
    <>        
        <Button onClick={() => { 
                // setUrlPdf(`${globalConfig.get().apiUrl}/Download/${fileToken}/${fileName}`); 
                setCurrentSearchPersonUsage(searchPersonUsage);                  
                setOpenPersonSearchFilter(true);
                closeSnackbar(snackbarId); }}>   
            <GrSearch size={24} />
        </Button>
        <Button onClick={() => { closeSnackbar(snackbarId) }}>
            <GrClose size={24} />
        </Button>
    </>
)

  useEffect( () => {     
             
    debouncedDefaultTaxRateChange();      
  }, [watchDefaultTaxRate]);

  useEffect( () => {     
    setCurrentSearchPersonUsage('customer');         
    debouncedCustomerChange();      
  }, [watchCustomerFirstName, watchCustomerLastName, watchCustomerCorporationName ,debouncedCustomerChange]);

  const refEnumItems = useRef<IEnumerationItem[]>();    
  useEffect( () => {   
      refEnumItems.current = enumItems;
    
  }, [enumItems]);

  const refEnumItems2 = useRef<IEnumerationItem[]>();    
  useEffect( () => {   
      refEnumItems2.current = enumItems2;
    
  }, [enumItems2]);


  
  useEffect( () => {   
    refProductIndex.current = productIndex;
  
  }, [productIndex]);

  
 
  useEffect( () => {   
    refShowroomId.current = watchShowroomId;
  
  }, [watchShowroomId]);

  useEffect( () => {   
    refCurrencyCode.current = watchCurrencyCode;
  
  }, [watchCurrencyCode]);

 
  useEffect( () => {        
    setCurrentFormNameAtom(t('Billing'));
    setCurrentBasicTextFilterProps(basicFilterBilling);
  }, []);    

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
    useEffect( () => {
      _setId(Number(id || 0));
    }, [id] );

  useEffect( () => {
    
      // setCurrentFormName(t('Billing'));        
      setCurrentFormNameAtom(_id>0?`${t('Billing')} - # ${_id} # -`: t('Billing') );

      if(_id > 0 && getValues().id !== _id) 
        retrieveData('Billing',_id, refetch);  
    }, [_id] );

  useEffect( () => {

    async function _() {   
      if(_data && _data.id > 0) {      //console.log(_data.customerBirthDate); console.log(typeof _data.customerBirthDate);
        
        if( !userStores.some(s => s.storeId === _data.showroomId) ) {
          enqueueSnackbar( `${t('This billing is not in your scope')} :: ${t('Showroom')}`, { variant: 'error',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          _newData();
          return;
        }

        const arr = await getProductOptionsByBilling(_data.id);
        
        const {billingDetails} = _data;
        reset( {..._data,
            billingDetails: billingDetails.map( x => 
                ({...x, productOptions: arr.filter(op => op.productId === x.productId) }) ),
            currentBillingDetailIndex: _data.billingDetails.length>0? 0:-1
        });
          
      }
    }

    _();        
    
  }, [_data, reset]);
  
    const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
         _newData(); 
    }

    const _newData = () => {
      _setId(0);      
      reset({...defaultBilling, showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0, 
            currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate});
    }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {     
        
        setTotalAmount();

        if(!checkEntitySaveAuthorization('Billing', _id)) {
          setIsSaveLoading(false);
          return;
        }
        const data = getValues(); 

        if(data.showroomId <= 0 || !userStores.some(s => s.storeId === data.showroomId && s.isShowroom) ) {
          enqueueSnackbar( `${t('Invalid data')} :: ${t('Showroom')}`, { variant: 'error',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        if( isFalsy(data.currencyCode) ) {
          enqueueSnackbar( `${t('Invalid data')} :: ${t('Currency')}`, { variant: 'error',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }
        
        if( (data.amount <= 0 || data.billingDetails.length <= 0) ) {
          setIsSaveLoading(false);
          return;
        }

        if( (!applicationSetup.isTaxRateVariable && data.defaultTaxRate !== 0  && data.defaultTaxRate !== applicationSetup.defaultTaxRate  ) ) {
          enqueueSnackbar( `${t('Invalid data')} :: ${t('Tax')} : ${data.defaultTaxRate}`, { variant: 'error',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 });
          setIsSaveLoading(false);
          return;
        }

        if( (data.customer.type === 'physical' && data.customer.firstName.trim() === '' && data.customer.lastName.trim() === '') ) {
          enqueueSnackbar( t('The customer is not specified'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        if( (data.customer.type === 'moral' && data.customer.corporationName.trim() === '' ) ) {
          enqueueSnackbar( t('The customer is not specified'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }
        
        if(data.billingDetails.some( b => b.type === 'service' && !b.beneficiaryIsCustomer 
            && isFalsy(b.firstName) && isFalsy(b.lastName) ) ) {
              enqueueSnackbar( t('The benificiary of service have to be defined'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        if(data.billingDetails.some( b => b.type === 'packaging' && 
              b.billingPackagingDetails.some(bp => bp.type === 'service' && !bp.beneficiaryIsCustomer 
                && isFalsy(bp.firstName) && isFalsy(bp.lastName) ) ) ) {
              enqueueSnackbar( t('The benificiary of service have to be defined'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        if(data.billingDetails.some( b => b.type === 'service' && b.isDelivered && b.billingServiceTasks.some(t => t.employeeId <= 0) ) ) {
            enqueueSnackbar( t('Employee of a task have to be defined'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
            
            if(!applicationSetup.allowServiceWithoutTechnician) {
              setIsSaveLoading(false);
              return;
            }
        }

        if(data.billingDetails.some( b => b.type === 'packaging' && b.isDelivered &&
            b.billingPackagingDetails.some(bp => bp.type === 'service' && bp.billingServiceTasks.some( t => t.employeeId <= 0) ) )) {
          enqueueSnackbar( t('Employee of a task have to be defined'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          
          if(!applicationSetup.allowServiceWithoutTechnician) {
            setIsSaveLoading(false);
            return;
          }          
      }

        mutate(data);
      }
    
      const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
        openEntityActionDrawer('Billing', _id);
      }

      const printData = async (event: MouseEvent<HTMLButtonElement>) => {      
        openEntityPrintDrawer('Billing', _id);
      }

     
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
      queryClient.invalidateQueries(['Billing',_id]);        
      await retrieveData('Billing',_id, refetch);        
      reset(_data);        
    }

    
  

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={0.5} columnSpacing={0.1}>
                  <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                      <Stack flexDirection='column'  >
                          <Box sx={{ mt: 0.25, width: '100%' }} > 
                            <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                  sx={{...typographyGroupBoxStyling}}>
                              {`${t(('Identification'))} `}
                            </Typography>                                                       
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} >
                            <ToggleButtonGroup value={watchCustomerPersonType} exclusive key={watchCustomerPersonType} size="small" 
                                    onChange={handleCustomerType} aria-label="text alignment" fullWidth >
                              <ToggleButton value="physical" aria-label="centered">
                                {t('Physical person')}
                              </ToggleButton>
                              <ToggleButton value="moral" aria-label="centered">
                                {t('Moral person')}
                              </ToggleButton>                                  
                            </ToggleButtonGroup>                                                             
                          </Box>
                                                                                                                                 
                          <Box sx={{ mt: 1, width: '100%' }} key={` person ${getValues().customerId}`}>
                            
                            { (watchCustomerPersonType === 'physical') && <TextField sx={{width:'calc(35% - 8px)'}} id="fn" label={lg.startsWith('fr')?t('Last name'):t('First name')} 
                              {...register(lg.startsWith('fr')?'customer.lastName':'customer.firstName')} inputProps={ {readOnly: (watchCustomerId>0) }} 
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">  
                                  { (isFalsy(watchCustomerId) ||  watchCustomerId<=0) ?
                                      <IconButton color="primary" onClick={(event: any) => handleClickOpenPerson(event, 'customer')}>
                                        <ArrowDropDownCircleIcon />
                                      </IconButton> : 
                                      <IconButton color="primary" onClick={(event: any) => handleClickRemovePerson(event, 'customer')}>
                                        <RemoveCircleIcon />
                                      </IconButton>
                                    }                                                                                                                                     
                                </InputAdornment>
                              ) 
                            }} /> }  
                            { (watchCustomerPersonType === 'physical') && <TextField sx={{width:'calc(35% - 8px)'}} id="ln" label={lg.startsWith('fr')?t('First name'):t('Last name')} 
                              {...register(lg.startsWith('fr')?'customer.firstName':'customer.lastName')} inputProps={ {readOnly: (watchCustomerId>0) }} /> } 
                            { (watchCustomerPersonType === 'moral') && <TextField sx={{width:'calc(45% - 8px)'}} id="ln" label={t('Name')} 
                              {...register('customer.corporationName')} inputProps={ {readOnly: (watchCustomerId>0) }}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">  
                                    { (isFalsy(watchCustomerId) ||  watchCustomerId<=0) ?
                                        <IconButton color="primary" onClick={(event: any) => handleClickOpenPerson(event, 'customer')}>
                                          <ArrowDropDownCircleIcon />
                                        </IconButton> : 
                                        <IconButton color="primary" onClick={(event: any) => handleClickRemovePerson(event, 'customer')}>
                                          <RemoveCircleIcon />
                                        </IconButton>
                                      }                                                                                                                                     
                                  </InputAdornment>
                                ) 
                              }} /> }
                            { (watchCustomerPersonType === 'moral') && <Controller name='customer.corporationType' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="customerCorporationType"
                                    label={t('Type')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_CORPORATION_TYPE ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                  </TextField>
                                )} />  }  
                            <Controller name={watchCustomerPersonType === 'physical'? 'customer.birthDateType': 'customer.creationDateType'} control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(12% - 8px)'}} id="customerBirthDateType"
                                    label={t('Date type')} inputProps={ {readOnly: false}} focused >
                                    {getBirthDateType().map( 
                                      (x,idx) => <MenuItem key={x.value} value={x.value}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                              />   
                            {(watchCustomerPersonType === 'physical') && <Controller key={`${watchCustomerBirthDateType} dob`} control={control}
                                name={'customer.birthDate'} 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('Birth date')} 
                                    onChange={onChange}                
                                    format={watchCustomerBirthDateType==='day'?'dd/MM':'dd/MM/yyyy'}
                                    value={watchCustomerBirthDateType==='none'?null: new Date(value)}
                                    views={watchCustomerBirthDateType==='day'?['month','day']: ['year', 'month', 'day']}
                                    disabled={watchCustomerBirthDateType==='none'}
                                    //format={watchBirthDateType==='day'?'MM/dd':'dd/MM/yyyy'}
                                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}

                                    //renderInput={(params) => <TextField {...params}  sx={{width:'calc(18% - 8px)'}} />}
                                    slotProps={{ textField: { sx: {width:'calc(18% - 8px)'}  }} }
                                  /> )}
                              />  }        
                              {(watchCustomerPersonType === 'moral') && <Controller key={`${watchCustomerCreationDateType} dob`} control={control}
                                name={'customer.creationDate'} 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('Creation date')} 
                                    onChange={onChange}                
                                    format={watchCustomerCreationDateType==='day'?'dd/MM':'dd/MM/yyyy'}
                                    value={watchCustomerCreationDateType==='none'?null:new Date(value)}
                                    views={watchCustomerCreationDateType==='day'?['month','day']: ['year', 'month', 'day']}
                                    disabled={watchCustomerCreationDateType==='none'}
                                    //format={watchBirthDateType==='day'?'MM/dd':'dd/MM/yyyy'}
                                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    //renderInput={(params) => <TextField {...params}  sx={{width:'calc(18% - 8px)'}} />}
                                    slotProps={{ textField: { sx: {width:'calc(18% - 8px)'}  }} }
                                  /> )}
                              />  }                  
                              { openPersonFilter && <FormDialog open={openPersonFilter} maxWidth='md'
                                okText='' cancelText='' title={t('Person')} onCancel={()=> {}} 
                                onClose={()=> {setOpenPersonFilter(false);}} onOk={()=> {setOpenPersonFilter(false);}}  >
                                    <BasicTextFilterForm<IPerson> {...basicFilterPerson } />
                            </FormDialog> }
                            { openPersonCopy && <FormDialog open={openPersonCopy} maxWidth='sm' height='50vh'
                                okText='' cancelText='' title={t('Person')} onCancel={()=> {}} 
                                onClose={()=> {setOpenPersonCopy(false);}} onOk={()=> {setOpenPersonCopy(false);}}  >
                                  <Box sx={{ mt: 0.25, width: '100%' }} >
                                    <EnhancedTable<IPerson> 
                                      rows={personsCopy.filter(p => !(isFalsy(p.firstName) && isFalsy(p.lastName)))} 
                                      headCells={[
                                        {id:lg.includes('en')?'firstName':'lastName', label : lg.includes('en')?t('First name'):t('Last name'),  display: true, type: 'string', width: 50 },
                                        {id:lg.includes('en')?'lastName':'firstName', label : lg.includes('en')?t('Last name'):t('First name'),  display: true, type: 'string', width: 50},                                        
                                      ]} 
                                      title={t(`Select the name to copy`)} objKey='id' 
                                      stateSelected={undefined} 
                                      onRowSelected={undefined} onRowDoubleClick={handlePersonCopyDoubleClick} rowCheckedMode='single'
                                      onRowCheckedSelectChange={undefined} order='desc' orderBy={lg.includes('en')?'lastName':'firstName'}
                                      
                                      toolbarActions={[
                                                              
                                      ]}
                                    />
                                  </Box>  
                            </FormDialog> }
                            
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} > 
                            <TextField sx={{width:'calc(15% - 8px)'}} id="customer.portable1" label={`${t('Portable')} 1`} 
                              {...register('customer.portable1')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }  />
                            <TextField sx={{width:'calc(15% - 8px)'}} id="customerPortable2" label={`${t('Portable')} 2`} 
                              {...register('customer.portable2')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>   
                            <TextField sx={{width:'calc(35% - 8px)'}} id="customerEmail1" label={`${t('Email')} 1`} 
                              {...register('customer.email1')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>  
                            <TextField sx={{width:'calc(35% - 8px)'}} id="customerEmail2" label={`${t('Email')} 2`} 
                              {...register('customer.email2')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>                                                    

                            { openPersonSearchFilter && <FormDialog open={openPersonSearchFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Person')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenPersonSearchFilter(false);}} onOk={()=> {setOpenPersonSearchFilter(false);}}  >
                                      <BasicTextFilterForm<IPerson> {...basicFilterPersonSearch } />
                              </FormDialog> } 
                          </Box>                          
                      </Stack>                        
                    </Grid>
                    <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                      <Stack flexDirection='column'  >
                          <Box sx={{ mt: 0.25, width: '100%' }} > 
                            <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                  sx={{...typographyGroupBoxStyling}}>
                              {`:: ${t(('Total'))} ::`}
                            </Typography>                                                       
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} >
                              <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                              <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                              <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                              <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />                             

                              <Button id='btnPrint' onClick={printData} sx={ {display:'none'}}  />
                              <Controller name='showroomId' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="showroomId"
                                    label={t('Showroom')} inputProps={ {readOnly: getValues().billingDetails.length>0 }} focused >
                                    {userStores && userStores.filter( s => s.isShowroom ).map( 
                                        (x,idx) => <MenuItem key={x.storeId} value={x.storeId}>{x.storeName}</MenuItem> )
                                      }
                                  </TextField>
                                )} />
                                <Controller name='currencyCode' control={control}                                     
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="currencyCode"
                                      label={t('Currency')} inputProps={ {readOnly: getValues().billingDetails.length>0}} >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_CURRENCY ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                              
                              <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={`${t('Default')} - ${t('Tax')}`} sx={{width:'calc(20% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={lg.includes('fr')?' ':','}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`defaultTaxRate`}
                                control={control}
                            />
                            
                          </Box>        
                          <Box sx={{ mt: 1, width: '100%' }} >
                            
                            <Controller name={watchIsProforma? 'proformaStatus' : 'status'} control={control}                                     
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="status"
                                      label={t('Status')} inputProps={ {readOnly: true}} >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                            <Controller control={control}
                              name='statusDate' 
                              render={({ field: { onChange, onBlur, value, ref } }) => (
                                <DatePicker label={t('Status date')} 
                                  onChange={onChange} disableOpenPicker readOnly                    
                                  value={new Date(value)}
                                  //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                                  slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                /> )}
                            />
                            <Controller name='statusPurpose' control={control}                                     
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="status"
                                      label={t('Status purpose')} inputProps={ {readOnly: true}} >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_BILLING_STATUS_PURPOSE ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                              <Controller control={control}
                                name='issueDate' 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('Issue date')} 
                                    onChange={onChange} disableOpenPicker readOnly                   
                                    value={new Date(value)}
                                    slotProps={{ textField: { sx: {width:'calc(15% - 8px)'} } }}
                                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                                  /> )}
                              />
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} >
                            <Controller
                              render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                return (
                                  <NumberFormat    
                                    label={t('Net')} sx={{width:'calc(20% - 8px)'}} disabled={true}
                                    allowEmptyFormatting={false}
                                    control={control}    
                                    thousandSeparator={lg.includes('fr')?' ':','}
                                    decimalScale={2}
                                    onValueChange={ (v) => onChange(v.floatValue) }
                                    defaultValue={value}
                                    value={value}
                                    customInput={TextFieldRight}                            
                                  />
                                );
                              }}
                              name={`netAmount`}
                              control={control}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Tax')} sx={{width:'calc(20% - 8px)'}} disabled={true}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={lg.includes('fr')?' ':','}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`tax`}
                                control={control}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Amount')} sx={{width:'calc(20% - 8px)'}} disabled={true}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={lg.includes('fr')?' ':','}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`amount`}
                                control={control}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Paid')} sx={{width:'calc(20% - 8px)'}} disabled={true}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={lg.includes('fr')?' ':','}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`amountPaid`}
                                control={control}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={ `${t('Unpaid')} : ${(watchStatus==='10' && value>0)? getValues().dueDate:''}`} 
                                      sx={{width:'calc(20% - 8px)'}} disabled={true}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={lg.includes('fr')?' ':','}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                   
                                    />
                                  );
                                }}
                                name={`unpaidAmount`}
                                control={control}
                            />
                          </Box>
                          
                      </Stack>                        
                    </Grid>
                    <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>
                        <Box key={`${'watchFileName'} - key`} sx={{ mt: 0.25, width: '100%' }} >  
                          <ArrayFieldTableEx<IBilling,IBillingDetail,'id'> 
                              mainObject={getValues()} fieldKey='id' 
                              headCells={headBillingDetailCells} rowsPathName='billingDetails' 
                              title={t('Add product(s)')} rowActionIcon={billingDetailRowActionIcon}  
                              onRowSelected={handleBillingDetailSelected}
                              onRowDoubleClick={handleBillingDetailRowDoubleClick}
                                                  
                              refAppend={refAppendBillingDetails as MutableRefObject<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>}
                              refUpdate={refUpdateBillingDetail as MutableRefObject<(index: number,value: Partial<FieldArray<IBilling>>) => void>}
                              refRemove={refRemoveBillingDetail as MutableRefObject<(index: number) => void>}

                              //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                              //displayMore={undefined}
                              toolbarActions={
                                (getValues().status === '95') ?
                                [
                                  { toolTip: `${t('Add')}... ${t('Service')}`, onClickIcon: handleAddServices ,icon: RoomServiceIcon, isThere: applicationSetup.hasService },
                                  { toolTip: `${t('Add')}... ${t('Fee')}`, onClickIcon: handleAddFees ,icon: MonetizationOnIcon, isThere: applicationSetup.hasFee },
                                  { toolTip: `${t('Add')}... ${t('Packaging')}`, onClickIcon: handleAddPackagings ,icon: ArchiveIcon, isThere: applicationSetup.hasPackaging },
                                  { toolTip: `${t('Add')}... ${t('Article')}`, onClickIcon: handleAddArticles ,icon: LocalMallIcon, isThere: applicationSetup.hasArticle  },
                                  { toolTip: `${t('Add')}... ${t('Formation')}`, onClickIcon: handleAddFormations ,icon: SchoolIcon, isThere: applicationSetup.hasFormation  },

                                  { toolTip: `${t('Add')}... ${t('Maintenance')}`, onClickIcon: handleAddMaintenances ,icon: BuildCircleOutlinedIcon, isThere: applicationSetup.hasMaintenance },
                                  { toolTip: `${t('Add')}... ${t('Contract')}`, onClickIcon: handleAddContracts ,icon: DescriptionIcon, isThere: applicationSetup.hasContract },

                                  { toolTip: `${t('Add')}... ${t('Rental')}`, onClickIcon: handleAddRentals ,icon: AssignmentIcon, isThere: applicationSetup.hasRental },
                                  { toolTip: `${t('Add')}... ${t('Consumption')}`, onClickIcon: handleAddConsumptions ,icon: FastfoodIcon, isThere: applicationSetup.hasConsumption },

                                  { toolTip: `${t('Add')}... ${t('Laundry')}`, onClickIcon: handleAddLaundries ,icon: WashIcon, isThere: applicationSetup.hasLaundry },
                                  { toolTip: `${t('Add')}... ${t('Exam')}`, onClickIcon: handleAddExams ,icon: MedicalServicesIcon, isThere: applicationSetup.hasExam },
                                  
                                ].filter(x => x.isThere).map( x => x) : []
                            } 
                              canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                          /> 
                          { openBillingDetailDuplicateDialog && <FormDialog open={openBillingDetailDuplicateDialog} maxWidth='xs' height='35vh'
                            okText={t('OK')} cancelText={t('Cancel')} title={t('Duplicate service')} onCancel={()=> {}} 
                            onClose={()=> {setOpenBillingDetailDuplicateDialog(false);}} onOk={handleOkBillingDetailDuplicate}  >
                              <Stack flexDirection='column'>
                                <Box sx={{ mt: 0.25, width: '100%' }} >
                                  <Controller                                
                                    render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                      return (
                                        <NumberFormat      
                                          decimalScale={3}
                                          label={`${t('Number of service')}`} sx={{width:'calc(100% - 8px)'}}
                                          allowEmptyFormatting={false}
                                          control={control}             
                                                        
                                          onValueChange={ (v) => onChange(v.floatValue) }              
                                          //fixedDecimalScale={true} 
                                          thousandSeparator={lg.includes('fr')?' ':','}
                                          //{...field}
                                          customInput={TextFieldRight}
                                          defaultValue={value}
                                          value={value}
                                          //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                        />
                                      );
                                    }}
                                    name={`serviceDuplicateCount`}
                                    control={control}
                                  />
                                </Box>
                              </Stack>

                            </FormDialog> }
                          { openProductFilter && <FormDialog open={openProductFilter} maxWidth='md'
                                okText='' cancelText='' title={t('Product')} onCancel={()=> {}} 
                                onClose={()=> {setOpenProductFilter(false);}} onOk={()=> {setOpenProductFilter(false);}}  >
                                    <BasicTextFilterForm<IProduct> {...basicFilterProduct } />
                            </FormDialog> }
                          { openServiceFilter && <FormDialog open={openServiceFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Service')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenServiceFilter(false);}} onOk={()=> {setOpenServiceFilter(false);}}  >
                                      <BasicTextFilterForm<IProduct> {...basicFilterService } />
                              </FormDialog> }
                         { openFeeFilter && <FormDialog open={openFeeFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Fee')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenFeeFilter(false);}} onOk={()=> {setOpenFeeFilter(false);}}  >
                                      <BasicTextFilterForm<IProduct> {...basicFilterFee } />
                              </FormDialog> }
                          { openArticleFilter && <FormDialog open={openArticleFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Article')} onCancel={()=> {}} 
                              onClose={()=> {setOpenArticleFilter(false);}} onOk={()=> {setOpenArticleFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterArticle } />
                          </FormDialog> }
                          { openPackagingFilter && <FormDialog open={openPackagingFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Packaging')} onCancel={()=> {}} 
                              onClose={()=> {setOpenPackagingFilter(false);}} onOk={()=> {setOpenPackagingFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterPackaging } />
                          </FormDialog> }
                          { openFormationFilter && <FormDialog open={openFormationFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Formation')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenFormationFilter(false);}} onOk={()=> {setOpenFormationFilter(false);}}  >
                                      <BasicTextFilterForm<IProduct> {...basicFilterFormation } />
                              </FormDialog> }
                          { openMaintenanceFilter && <FormDialog open={openMaintenanceFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Maintenance')} onCancel={()=> {}} 
                              onClose={()=> {setOpenMaintenanceFilter(false);}} onOk={()=> {setOpenMaintenanceFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterMaintenance } />
                          </FormDialog> }
                          { openContractFilter && <FormDialog open={openContractFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Contract')} onCancel={()=> {}} 
                              onClose={()=> {setOpenContractFilter(false);}} onOk={()=> {setOpenContractFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterContract } />
                          </FormDialog> }
                          { openRentalFilter && <FormDialog open={openRentalFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Rental')} onCancel={()=> {}} 
                              onClose={()=> {setOpenRentalFilter(false);}} onOk={()=> {setOpenRentalFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterRental } />
                          </FormDialog> }
                          { openConsumptionFilter && <FormDialog open={openConsumptionFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Consumption')} onCancel={()=> {}} 
                              onClose={()=> {setOpenConsumptionFilter(false);}} onOk={()=> {setOpenConsumptionFilter(false);}}  >
                                  <BasicTextFilterForm<IProduct> {...basicFilterConsumption } />
                          </FormDialog> }
                          
                          { openEmployeeFilter && <FormDialog open={openEmployeeFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Employee')} onCancel={()=> {}} 
                              onClose={()=> {setOpenEmployeeFilter(false);}} onOk={()=> {setOpenEmployeeFilter(false);}}  >
                                  <BasicTextFilterForm<IEmployee> {...basicFilterEmployee } />
                          </FormDialog> }
                          { productIndex>=0 && openBillingDetailServiceDelivery && <FormDialog open={openBillingDetailServiceDelivery} maxWidth='sm' height='35vh'
                                  okText={t('OK')} cancelText={t('Cancel')} title={t('Service execution date')} 
                                  onCancel={()=> {setOpenBillingDetailServiceDelivery(false);}} 
                                  onClose={()=> {setOpenBillingDetailServiceDelivery (false);}} onOk={handleOkBillingDetailServiceDelivery}  >
                                      <Stack flexDirection='column'>
                                        <Box sx={{ mt: 0.25, width: '100%' }} > 
                                          <FormControlLabel sx={{width:'calc(35% - 8px)'}}
                                              label={`${t('Is executed ?')}`}
                                              control={
                                              <Controller
                                                  name={`billingDetails.${productIndex}.isDelivered`}  
                                                  control={control}
                                                  render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                          />} />
                                          { <FormControlLabel sx={{width:'calc(35% - 8px)'}}
                                              label={`${t('Is execution date define ?')}`}
                                              control={
                                              <Controller
                                                  name={`billingDetails.${productIndex}.isDeliveryDateDefine`}  
                                                  control={control}
                                                  render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                          />} /> }
                                          <Controller key={`${getValues().billingDetails[productIndex].deliveryDate}  -`}  
                                              render={({ field: { onChange, onBlur, value, ref } }) => (
                                                <DatePicker label={t('Date of execution')} key={`${getValues().billingDetails[productIndex].deliveryDate} dob- x`}
                                                  onChange={onChange}                
                                                  //inputFormat={getValues().billingDetails[productIndex].birthDateType==='day'?'dd/MM':'dd/MM/yyyy'}
                                                  value={new Date(value)}
                                                  slotProps={{ textField: { sx: {width:'calc(30% - 8px)'}  }} }
                                                  //renderInput={(params) => <TextField {...params}  sx={{width:'calc(30% - 8px)'}} />}
                                                /> )}
                                              name={`billingDetails.${productIndex}.deliveryDate`}    
                                              control={control}
                                            />
                                        </Box>
                                      </Stack>
                              </FormDialog> }
                        </Box>
                        <Box sx={{ mt: 2.25, width: '100%' }} > 
                          <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                sx={{...typographyGroupBoxStyling}}>
                            {`${t('Commission')} ... ${t('Number')}`}
                          </Typography>                                                       
                        </Box> 
                        <Box sx={{ mt: 0.25, width: '100%' }} key={` employee ${getValues().businessEmployeeId}`}> 
                          <TextField sx={{width:'calc(50% - 8px)'}} id="businessEmployeeFullName" label={t('Employee')} 
                              {...register('businessEmployeeFullName')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">  
                                  { (isFalsy(watchBusinessEmployeeId) ||  watchBusinessEmployeeId<=0) ?
                                      <IconButton color="primary" onClick={(event: any) => handleClickSelectEmployee(event, 'business')}>
                                        <ArrowDropDownCircleIcon />
                                      </IconButton> : 
                                      <IconButton color="primary" onClick={(event: any) => handleClickResetEmployee(event, 'business')}>
                                        <RemoveCircleIcon />
                                      </IconButton>
                                    }                                                                                                                                     
                                </InputAdornment>
                              ) 
                            }} />        
                            <TextField sx={{width:'calc(50% - 8px)'}} id="businessEmployeeManagerFullName" label={t('Manager')} 
                              {...register('businessEmployeeManagerFullName')} inputProps={ {readOnly: true, autoComplete: 'new-password', style: {textTransform: 'none'} } }
                            />                                              
                        </Box>
                        <Box sx={{ mt: 0.25, width: '100%' }} key={` proformaNumber ${getValues().businessEmployeeId}`}> 
                          <TextField sx={{width:'calc(30% - 8px)'}} id="proformaNumber" label={t('Proforma number')} 
                              {...register('proformaNumber')} inputProps={ {readOnly: true, autoComplete: 'new-password', style: {textTransform: 'none'} } } 
                              helperText={`${(watchStatus==='95' && !watchHasNumber && watchId>0)?t('Activate proforma numbering'):''}`} />        
                          <TextField sx={{width:'calc(30% - 8px)'}} id="billingNumber" label={t('Billing number')} 
                            {...register('billingNumber')} inputProps={ {readOnly: true, autoComplete: 'new-password', style: {textTransform: 'none'} } } />                                              
                          <TextField sx={{width:'calc(40% - 8px)'}} id="billingRecipientName" label={t('Billing recipient')} 
                            {...register('billingRecipientName')} inputProps={ {readOnly: false, autoComplete: 'new-password', style: {textTransform: 'none'} } } />
                        </Box>
                        <Box sx={{ mt: 0.25, width: '100%' }} key={` proforma term an reference ${getValues().businessEmployeeId}`}> 
                          <Controller name='termAndConditionCode' control={control}                                     
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(100% - 8px)'}} id="status"
                                      label={t('Terms and conditions')} inputProps={ {readOnly: false}} 
                                      helperText={ 
                                        <FormHelperText>                                    
                                          { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_BILLING_TERM_REFERENCE, 
                                                                t('Term and condition'), t('Term and condition'))}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                            {t('Add')}
                                          </Link> }
                                          { (canUpdateEnumerationItem && !isFalsy(watchTermAndConditionCode) ) && <Link href="#" onClick={(event) => {
                                                 onUpdateItemClick( Enum_BILLING_TERM_REFERENCE, t('Term and condition'), t('Term and condition'),
                                                        refEnumItems.current?.find(x => x.enumerationCode === Enum_BILLING_TERM_REFERENCE && x.code === watchTermAndConditionCode)
                                                      ); }}
                                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                            {t('Update')}
                                          </Link> }
                                        </FormHelperText>
                                      }>
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_BILLING_TERM_REFERENCE ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                          {/* <TextField sx={{width:'calc(100% - 8px)'}} id="termAndCondition" label={t('Terms and conditions')} 
                              {...register('termAndCondition')} inputProps={ {readOnly: false, autoComplete: 'new-password', style: {textTransform: 'none'} } } />                                   */}
                        </Box>
                        <Box display='flex' flexDirection='row' alignContent='space-around' justifyContent='center' sx={{ mt: 1.5, width: '100%' }} > 
                          <Button variant="outlined" onClick={handleLinkClickBillingInfo} sx={{mb:2, ml: 1 }}>
                             {t('More infos')} ...                             
                          </Button>     
                          { openBillingInfo && <FormDialog open={openBillingInfo} maxWidth='md'
                                okText='' cancelText='' title={t('Informations on billing')} onCancel={()=> {}} 
                                onClose={()=> {setOpenBillingInfo(false);}} onOk={()=> {setOpenBillingInfo(false);}} >
                                  <BasicBillingInfoForm control={control} setValue={setValue} getValues={getValues} watch={watch} />
                            </FormDialog>}                                   
                        </Box>
                        { (getValues().status === '10' ) && 
                        <Box display='flex' flexDirection='row' alignContent='space-around' justifyContent='center' sx={{ mt: 1.5, width: '100%' }} > 
                          <Button variant="outlined" onClick={handleClickCommissionDistribution} sx={{mb:2, ml: 1 }}>
                             {t('Display commissions distribution')} ...                             
                          </Button>   
                          { openCommissionDistribution && <FormDialog open={openCommissionDistribution} maxWidth='sm' height='40vh'
                              okText='' cancelText='' title={t('Commission')} onCancel={()=> {}} 
                              onClose={()=> {setOpenCommissionDistribution(false);}} onOk={()=> {setOpenCommissionDistribution(false);}}  >
                                  <Stack flexDirection='column'>
                                    <Box sx={{ mt: 0.25, width: '100%' }} >
                                      <EnhancedTable<ICommissionDistribution> rows={commissionDistributions} 
                                            headCells={[            

                                              {id:lg.includes('en')?'firstName':'lastName', label : lg.includes('en')?t('First name'):t('Last name'),  display: true, type: 'string', width: 40 },
                                              {id:lg.includes('en')?'lastName':'firstName', label : lg.includes('en')?t('Last name'):t('First name'),  display: true, type: 'string', width: 40},
                                                
                                              {id:'amount', label : t('Amount'),  display: true, type: 'numeric', decimalScale: 4, width: 20},
                                             
                                              
                                            ]} 
                                            title={t(`Amount per product type`)} objKey='type' 
                                            stateSelected={undefined} 
                                            onRowSelected={undefined} rowCheckedMode='single'
                                            onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                                            onRowDoubleClick={undefined} 
                                            rowActionIcon={undefined}
                                            toolbarActions={[
                                              // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                                            ]}
                                          />
                                    </Box>
                                  </Stack>
                          </FormDialog> }                                     
                        </Box> }                                                
                      </Stack>
                    </Grid>     
                    <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>
                        <BasicBillingDetailForm control={control} getValues={getValues} setValue={setValue} watch={watch}  />
                      </Stack>
                    </Grid>                            
                </Grid>
            </Box>
        </FormProvider> 
  )
}

